import { datadogLogs } from '@datadog/browser-logs';
import { useExperiment } from '@statsig/react-bindings';
import { formatNumberAsCurrency } from 'helpers/utils';
import { useMemo } from 'react';
import { roundTwoDigits } from 'screens/Checkout/Form/utils';

interface DollarTierConfig {
  isTipValueModePercent: boolean;
  tipDollarAmountValues: number[];
  tipPercentValues: number[];
}

type Experiment = ReturnType<typeof useExperiment>;

// no experiment running now,
// using this as a placeholder for when we run our next amounts experiment
export const TIP_AMOUNTS_EXPERIMENT_NAME = '';
const DEFAULT_LOW_DOLLAR_CONFIG = {
  isTipValueModePercent: false,
  tipDollarAmountValues: [2, 3, 4],
  tipPercentValues: [],
};
const DEFAULT_MED_DOLLAR_CONFIG = {
  isTipValueModePercent: true,
  tipDollarAmountValues: [],
  tipPercentValues: [0.08, 0.1, 0.12],
};
const DEFAULT_HIGH_DOLLAR_CONFIG = {
  isTipValueModePercent: true,
  tipDollarAmountValues: [],
  tipPercentValues: [0.01, 0.02, 0.03],
};
const VAR2_HIGH_DOLLAR_CONFIG = {
  isTipValueModePercent: false,
  tipDollarAmountValues: [100, 200, 300],
  tipPercentValues: [],
};

const validateTipOptionsConfig = (
  config: DollarTierConfig,
  experimentGroupName: string | null,
  tier: string
) => {
  let isValid = true;
  const fallbackConfig =
    tier === 'low'
      ? DEFAULT_LOW_DOLLAR_CONFIG
      : tier === 'medium'
        ? DEFAULT_MED_DOLLAR_CONFIG
        : DEFAULT_HIGH_DOLLAR_CONFIG;
  const logData = {
    experiment: TIP_AMOUNTS_EXPERIMENT_NAME,
    groupName: experimentGroupName,
    tier,
    mode: config.isTipValueModePercent ? 'percent' : 'dollar',
    config,
  };
  if (config.isTipValueModePercent) {
    if (config.tipPercentValues?.length !== 3) {
      isValid = false;
      datadogLogs.logger.error(
        `Tip amounts config is missing required percent values. Falling back to default ${tier} dollar config.`,
        logData
      );
    } else if (config.tipPercentValues.some(value => typeof value !== 'number' || value <= 0)) {
      isValid = false;
      datadogLogs.logger.error(
        `Tip amounts config contains invalid percent values. Falling back to default ${tier} dollar config.`,
        logData
      );
    }
  } else {
    if (config.tipDollarAmountValues?.length !== 3) {
      isValid = false;
      datadogLogs.logger.error(
        `Tip amounts config is missing required dollar amount values. Falling back to default ${tier} dollar config.`,
        logData
      );
    } else if (
      config.tipDollarAmountValues.some(value => typeof value !== 'number' || value <= 0)
    ) {
      isValid = false;
      datadogLogs.logger.error(
        `Tip amounts config contains invalid amount values. Falling back to default ${tier} dollar config.`,
        logData
      );
    }
  }

  return isValid ? config : fallbackConfig;
};

const getTipOptionsConfig = (
  donationAmount: number,
  isVariant2: boolean,
  experiment: Experiment
) => {
  const lowDollarConfig = experiment.get(
    'low_dollar_config',
    DEFAULT_LOW_DOLLAR_CONFIG
  ) as DollarTierConfig;
  const medDollarConfig = experiment.get(
    'med_dollar_config',
    DEFAULT_MED_DOLLAR_CONFIG
  ) as DollarTierConfig;
  const highDollarConfig = experiment.get(
    'high_dollar_config',
    DEFAULT_HIGH_DOLLAR_CONFIG
  ) as DollarTierConfig;
  let config = medDollarConfig;
  let tier = 'medium';

  if (donationAmount < 20) {
    config = lowDollarConfig;
    tier = 'low';
  } else if (donationAmount >= 10000 && isVariant2) {
    config = VAR2_HIGH_DOLLAR_CONFIG;
    tier = 'high_var_2';
  } else if (donationAmount > 5000) {
    config = highDollarConfig;
    tier = 'high';
  }

  return validateTipOptionsConfig(config, experiment.groupName, tier);
};

const getTipOptionsFromExperiment = (
  donationAmount: number,
  isVariant2: boolean,
  experiment: Experiment
) => {
  const config = getTipOptionsConfig(donationAmount, isVariant2, experiment);

  if (config.isTipValueModePercent) {
    return config.tipPercentValues.map((percentValue: number) => ({
      percent: percentValue,
      amount: roundTwoDigits(percentValue * donationAmount),
      display: `${Math.round(percentValue * 100)}%`,
    }));
  }
  return config.tipDollarAmountValues.map((amountValue: number) => ({
    percent: null,
    amount: amountValue,
    display: formatNumberAsCurrency(amountValue),
  }));
};

export const useExperimentTipAmountOptions = (donationAmount: number, isVariant2: boolean) => {
  const experimentTipAmounts = useExperiment(TIP_AMOUNTS_EXPERIMENT_NAME);
  const options = useMemo(
    () => getTipOptionsFromExperiment(donationAmount, isVariant2, experimentTipAmounts),
    [donationAmount, isVariant2, experimentTipAmounts.groupName]
  );
  return { options, groupName: experimentTipAmounts.groupName };
};

export const useDefaultTipAmount = (donationAmount: number, isTippingVariant2: boolean) => {
  return useExperimentTipAmountOptions(donationAmount, isTippingVariant2).options[1].amount;
};
